import type { Notification, NotificationType } from "@/types";

/** Human-readable labels for notification types */
export const NOTIFICATION_TYPE_LABELS: Record<NotificationType, string> = {
  flow_completed: "Flow Completed",
  flow_failed: "Flow Failed",
  flow_paused: "Flow Paused",
  chat_shared: "Chat Shared",
  brainstorm_shared: "Brainstorm Shared",
  data_sync_completed: "Data Sync Completed",
  data_sync_failed: "Data Sync Failed",
  scheduled_flow_triggered: "Scheduled Flow Triggered",
  task_completed: "Task Completed",
  task_failed: "Task Failed",
};

/** Color classes for notification type badges (text + bg) */
export const NOTIFICATION_TYPE_BADGE_COLORS: Record<NotificationType, string> = {
  flow_completed: "text-green-400 bg-green-400/10",
  flow_failed: "text-red-400 bg-red-400/10",
  flow_paused: "text-yellow-400 bg-yellow-400/10",
  chat_shared: "text-blue-400 bg-blue-400/10",
  brainstorm_shared: "text-blue-400 bg-blue-400/10",
  data_sync_completed: "text-green-400 bg-green-400/10",
  data_sync_failed: "text-red-400 bg-red-400/10",
  scheduled_flow_triggered: "text-purple-400 bg-purple-400/10",
  task_completed: "text-green-400 bg-green-400/10",
  task_failed: "text-red-400 bg-red-400/10",
};

/** Text-only color classes for notification types */
export const NOTIFICATION_TYPE_COLORS: Record<NotificationType, string> = {
  flow_completed: "text-green-400",
  flow_failed: "text-red-400",
  flow_paused: "text-yellow-400",
  chat_shared: "text-blue-400",
  brainstorm_shared: "text-blue-400",
  data_sync_completed: "text-green-400",
  data_sync_failed: "text-red-400",
  scheduled_flow_triggered: "text-purple-400",
  task_completed: "text-green-400",
  task_failed: "text-red-400",
};

/** Notification type metadata for the Settings page */
export const NOTIFICATION_TYPES: {
  type: NotificationType;
  label: string;
  description: string;
}[] = [
  { type: "flow_completed", label: "Flow Completed", description: "When a flow finishes running successfully" },
  { type: "flow_failed", label: "Flow Failed", description: "When a flow encounters an error" },
  { type: "flow_paused", label: "Flow Paused", description: "When a flow is paused for review" },
  { type: "chat_shared", label: "Chat Shared", description: "When someone shares a chat with you" },
  { type: "brainstorm_shared", label: "Brainstorm Shared", description: "When someone shares a brainstorm with you" },
  { type: "data_sync_completed", label: "Data Sync Completed", description: "When a data source finishes syncing" },
  { type: "data_sync_failed", label: "Data Sync Failed", description: "When a data source sync fails" },
  {
    type: "scheduled_flow_triggered",
    label: "Scheduled Flow Triggered",
    description: "When a scheduled flow starts running",
  },
];

/** Filter options for the Notifications page */
export const NOTIFICATION_FILTER_OPTIONS: { value: "all" | NotificationType; label: string }[] = [
  { value: "all", label: "All" },
  ...NOTIFICATION_TYPES.map(({ type, label }) => ({ value: type, label })),
];

/** Notification types that share a chat or brainstorm session with the user */
export const SHARED_SESSION_NOTIFICATION_TYPES = new Set<NotificationType>(["chat_shared", "brainstorm_shared"]);

/**
 * Format a relative time string from a date string.
 * Handles NaN from invalid date strings gracefully.
 */
export function timeAgo(dateStr: string): string {
  const date = new Date(dateStr);
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);

  if (Number.isNaN(seconds)) return "";

  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;

  const now = new Date();
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: date.getFullYear() === now.getFullYear() ? undefined : "numeric",
  });
}

/**
 * Derive a frontend route from a notification's type and metadata.
 * Returns `null` when the notification type has no meaningful destination
 * or the required metadata fields are missing.
 */
export function getNotificationRoute(notification: Notification): string | null {
  const m = notification.metadata;

  switch (notification.type) {
    // Flow lifecycle → flow run detail page
    case "flow_completed":
    case "flow_failed":
    case "flow_paused": {
      const flowUuid = m.flow_uuid as string | undefined;
      const runUuid = m.flow_run_uuid as string | undefined;
      if (flowUuid && runUuid) return `/flows/${flowUuid}/runs/${runUuid}`;
      return null;
    }

    // Task lifecycle → task detail page
    case "task_completed":
    case "task_failed": {
      const taskUuid = m.task_uuid as string | undefined;
      if (taskUuid) return `/tasks/${taskUuid}`;
      return null;
    }

    // Sharing → the shared session
    case "chat_shared": {
      const sessionUuid = m.session_uuid as string | undefined;
      if (sessionUuid) return `/chat/${sessionUuid}`;
      return null;
    }
    case "brainstorm_shared": {
      const sessionUuid = m.session_uuid as string | undefined;
      if (sessionUuid) return `/group-chat/${sessionUuid}`;
      return null;
    }

    // Data sync → data source detail page
    case "data_sync_completed":
    case "data_sync_failed": {
      const collectionUuid = m.collection_uuid as string | undefined;
      if (collectionUuid) return `/data-sources/${collectionUuid}`;
      return null;
    }

    // Scheduled flow → flow detail page (no specific run yet)
    case "scheduled_flow_triggered": {
      const flowUuid = m.flow_uuid as string | undefined;
      if (flowUuid) return `/flows/${flowUuid}`;
      return null;
    }

    default:
      return null;
  }
}
